import useScrollLock from '@/hooks/useScrollLock';
import { useState, useRef } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { useDarkMode } from '@/hooks/useDarkMode';
import { Turnstile } from '@marsidev/react-turnstile';

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function AuthModal({ onClose, setUser }) {
  const darkMode = useDarkMode();
  useScrollLock(true);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    acceptedTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [twoFAChallengeId, setTwoFAChallengeId] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('Enter your email address first');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { email: formData.email });
      setForgotSent(true);
      toast.success(res.data.message);
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Too many requests, please wait a minute and try again');
      } else {
        toast.error(error.response?.data?.detail || 'Could not send the reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const finishLogin = (data) => {
    // Token is set by the backend as an httpOnly `mementa_token` cookie.
    // Only cache the (non-sensitive) user object for fast first-paint.
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
    onClose();
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/verify-2fa', {
        challenge_id: twoFAChallengeId,
        code: twoFACode
      });
      finishLogin(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Verification failed';
      toast.error(detail);
      if (String(detail).includes('sign in again')) {
        setTwoFAChallengeId(null);
        setTwoFACode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      toast.error('Enter your email address above first');
      return;
    }
    setResending(true);
    try {
      const res = await api.post('/auth/resend-verification-public', { email: formData.email });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not resend the email. Please try again shortly.');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLogin && !formData.acceptedTerms) {
      toast.error('You must accept the Terms of Service to create an account');
      return;
    }

    if (!isLogin && TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error('Please wait for the verification check to complete');
      return;
    }
    
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : { ...formData, turnstile_token: turnstileToken };
      
      const response = await api.post(endpoint, payload);

      // Admin accounts require an emailed 2FA code before a session is issued.
      if (isLogin && response.data.requires_2fa) {
        setTwoFAChallengeId(response.data.challenge_id);
        toast.info(response.data.message);
        return;
      }

      finishLogin(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
      // 403 on login = correct password but unverified email → show resend UI.
      if (isLogin) {
        setNeedsVerification(error.response?.status === 403);
      }
      // Turnstile tokens are single-use, get a fresh one for the retry.
      if (!isLogin && turnstileRef.current) {
        turnstileRef.current.reset();
        setTurnstileToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center" 
      style={{
        background: darkMode.isDark ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.5)', 
        backdropFilter: 'blur(4px)',
        zIndex: 99999,
        pointerEvents: 'auto'
      }}
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-md p-8 rounded-2xl transition-colors" 
        style={{
          background: darkMode.bgSecondary, 
          boxShadow: darkMode.isDark ? '0 20px 60px rgba(0, 0, 0, 0.6)' : '0 20px 60px rgba(0, 0, 0, 0.3)',
          pointerEvents: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="close-auth-modal"
          className="absolute top-4 right-4 transition-colors"
          style={{color: darkMode.textSecondary}}
        >
          <X className="w-6 h-6" />
        </button>

        <h2 
          className="text-3xl mb-6 transition-colors" 
          style={{
            fontFamily: 'Playfair Display, serif', 
            color: darkMode.textPrimary
          }}
        >
          {twoFAChallengeId ? 'Check Your Email' : forgotMode ? 'Reset Password' : isLogin ? 'Welcome Back' : 'Create Account'}
        </h2>

        {forgotMode && !twoFAChallengeId ? (
          <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-password-form">
            {forgotSent ? (
              <div
                className="rounded-lg p-4 text-sm"
                style={{ background: darkMode.isDark ? 'rgba(254,215,165,0.12)' : '#FFF4E5', border: '1px solid #FED7A5' }}
                data-testid="forgot-password-sent-notice"
              >
                <p className="font-medium mb-1" style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}>
                  Check your inbox
                </p>
                <p style={{ color: darkMode.textSecondary }}>
                  If an account exists for {formData.email}, a reset link is on its way. It expires in 1 hour.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm" style={{ color: darkMode.textSecondary }}>
                  Enter your email and we&rsquo;ll send you a link to choose a new password.
                </p>
                <div>
                  <Label htmlFor="forgot-email" style={{color: darkMode.textSecondary}}>Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    data-testid="forgot-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    autoFocus
                    className="mt-1 transition-colors"
                    style={{ color: darkMode.textPrimary, background: darkMode.bgPrimary, borderColor: darkMode.borderPrimary }}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-password-submit"
                  className="w-full"
                  style={{ background: '#9E6752', color: 'white', borderRadius: '20px' }}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </>
            )}
            <button
              type="button"
              onClick={() => { setForgotMode(false); setForgotSent(false); }}
              data-testid="forgot-password-back"
              className="w-full text-sm"
              style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}
            >
              ← Back to sign in
            </button>
          </form>
        ) : twoFAChallengeId ? (
          <form onSubmit={handleVerify2FA} className="space-y-4" data-testid="auth-2fa-form">
            <p className="text-sm" style={{ color: darkMode.textSecondary }}>
              Admin sign-in requires a verification code. Enter the 6-digit code we just emailed you.
            </p>
            <Input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={twoFACode}
              onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              required
              autoFocus
              data-testid="auth-2fa-code-input"
              className="text-center text-2xl tracking-[0.5em] font-mono"
              style={{ color: darkMode.textPrimary, background: darkMode.bgPrimary, borderColor: darkMode.borderPrimary }}
            />
            <Button
              type="submit"
              disabled={loading || twoFACode.length !== 6}
              data-testid="auth-2fa-verify-button"
              className="w-full"
              style={{ background: '#9E6752', color: 'white', borderRadius: '20px' }}
            >
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </Button>
            <button
              type="button"
              onClick={() => { setTwoFAChallengeId(null); setTwoFACode(''); }}
              data-testid="auth-2fa-back-button"
              className="w-full text-sm"
              style={{ color: '#9E6752' }}
            >
              ← Back to login
            </button>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <Label htmlFor="name" style={{color: darkMode.textSecondary}}>Name</Label>
              <Input
                id="name"
                data-testid="auth-name-input"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="mt-1 transition-colors"
                style={{
                  color: darkMode.textPrimary, 
                  background: darkMode.bgPrimary,
                  borderColor: darkMode.borderPrimary
                }}
              />
            </div>
          )}

          <div>
            <Label htmlFor="email" style={{color: darkMode.textSecondary}}>Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="auth-email-input"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              className="mt-1 transition-colors"
              style={{
                color: darkMode.textPrimary, 
                background: darkMode.bgPrimary,
                borderColor: darkMode.borderPrimary
              }}
            />
          </div>

          <div>
            <Label htmlFor="password" style={{color: darkMode.textSecondary}}>Password</Label>
            <PasswordInput
              id="password"
              testId="auth-password-input"
              toggleTestId="auth-password-toggle"
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="mt-1 transition-colors"
              style={{
                color: darkMode.textPrimary,
                background: darkMode.bgPrimary,
                borderColor: darkMode.borderPrimary
              }}
              iconColor={darkMode.textSecondary}
            />
            {isLogin && (
              <button
                type="button"
                onClick={() => { setForgotMode(true); setForgotSent(false); }}
                data-testid="forgot-password-link"
                className="mt-2 text-sm font-medium transition-colors hover:underline"
                style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}
              >
                Forgot password?
              </button>
            )}
          </div>

          {!isLogin && (
            <div className="flex items-start gap-2 pt-2">
              <input
                type="checkbox"
                id="terms"
                checked={formData.acceptedTerms}
                onChange={(e) => setFormData({...formData, acceptedTerms: e.target.checked})}
                className="mt-1 w-4 h-4 rounded border-gray-300"
                style={{accentColor: '#9E6752'}}
              />
              <label htmlFor="terms" className="text-sm transition-colors" style={{color: darkMode.textSecondary}}>
                I agree to the{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium transition-colors"
                  style={{color: darkMode.isDark ? '#FED7A5' : '#9E6752'}}
                >
                  Terms of Service
                </a>
              </label>
            </div>
          )}

          {isLogin && needsVerification && (
            <div
              data-testid="verification-required-notice"
              className="rounded-lg p-4 text-sm"
              style={{ background: darkMode.isDark ? 'rgba(254,215,165,0.12)' : '#FFF4E5', border: '1px solid #FED7A5' }}
            >
              <p className="font-medium mb-2" style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}>
                Your email isn't verified yet
              </p>
              <p className="mb-3" style={{ color: darkMode.textSecondary }}>
                Check your inbox (and spam folder) for the verification link, or request a new one.
              </p>
              <Button
                type="button"
                onClick={handleResendVerification}
                disabled={resending}
                data-testid="resend-verification-button"
                className="w-full"
                style={{ background: '#9E6752', color: 'white', borderRadius: '20px' }}
              >
                {resending ? 'Sending...' : 'Resend verification email'}
              </Button>
            </div>
          )}

          {!isLogin && TURNSTILE_SITE_KEY && (
            <div className="pt-2 flex justify-center" data-testid="auth-turnstile-widget">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                options={{ theme: darkMode.isDark ? 'dark' : 'light' }}
              />
            </div>
          )}

          <Button
            type="submit"
            data-testid="auth-submit-button"
            disabled={loading}
            className="w-full mt-6"
            style={{background: '#9E6752', color: 'white', borderRadius: '20px', padding: '12px'}}
          >
            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
          </Button>
        </form>
        )}

        {!twoFAChallengeId && !forgotMode && (
        <p className="mt-6 text-center text-sm" style={{color: darkMode.isDark ? '#D4A080' : '#534145'}}>
          {isLogin ? "Don't have an account?" : "Already have an account?"}
          <button
            onClick={() => { setIsLogin(!isLogin); setNeedsVerification(false); }}
            data-testid="toggle-auth-mode"
            className="ml-2 font-medium transition-colors"
            style={{color: darkMode.isDark ? '#FED7A5' : '#9E6752'}}
          >
            {isLogin ? 'Sign Up' : 'Sign In'}
          </button>
        </p>
        )}
      </div>
    </div>
  );
}